<!-- src/views/Restaurante/ZonasMesaList.vue -->
<template>
  <div class="p-3 sm:p-6 text-lexend overflow-x-hidden min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-900/80">
    
    <!-- HEADER PREMIUM -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1.5">
          <div class="w-10 h-1.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-blue-600 dark:text-blue-400 tracking-widest uppercase">Módulo Restaurante</span>
        </div>
        <h1 class="text-2xl sm:text-4xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-3">
          Zonas de Mesa
          <span class="px-3 py-1 bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 text-xs sm:text-sm font-black rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
            {{ zonas.length }}
          </span>
        </h1>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium">Administra las áreas y mesas de tu local</p>
      </div>
      
      <div class="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
        <div class="flex bg-white dark:bg-gray-800 p-1.5 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hidden sm:flex">
          <button @click="viewMode = 'table'" :class="['p-2 rounded-lg transition-all flex items-center gap-1', viewMode === 'table' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-gray-400 hover:text-gray-600']">
            <Icon icon="mdi:table" class="text-lg" />
          </button>
          <button @click="viewMode = 'cards'" :class="['p-2 rounded-lg transition-all flex items-center gap-1', viewMode === 'cards' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-gray-400 hover:text-gray-600']">
            <Icon icon="mdi:view-grid" class="text-lg" />
          </button>
        </div>
        
        <button @click="openCreateModal" class="w-full sm:w-auto px-4 sm:px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-black text-xs sm:text-sm tracking-wide transition-all shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2">
          <Icon icon="mdi:plus-circle" class="text-lg" />
          <span>Nueva Zona</span>
        </button>
      </div>
    </div>

    <!-- FILTROS -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 mb-6 animate-fadeInUp">
      <div class="flex flex-col sm:flex-row gap-4 items-end">
        <div class="flex-1 w-full">
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 ml-1 uppercase">Buscar Zonas</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl group-focus-within:text-blue-500 transition-colors" />
            <input v-model="searchQuery" type="text" placeholder="Buscar por nombre o descripción..." class="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-transparent focus:border-blue-500 focus:bg-white dark:focus:bg-gray-800 rounded-xl transition-all outline-none dark:text-white font-bold text-sm" />
          </div>
        </div>
        
        <div class="w-full sm:w-48">
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 ml-1 uppercase">Estado</label>
          <select v-model="filterStatus" class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-transparent focus:border-blue-500 rounded-xl font-bold text-gray-700 dark:text-gray-200 outline-none transition-all cursor-pointer text-sm appearance-none">
            <option value="all">Todos los estados</option>
            <option value="activos">Solo Activos</option>
            <option value="inactivos">Solo Inactivos</option>
          </select>
        </div>

        <button @click="cargarZonas" class="w-full sm:w-auto px-5 py-3 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-xs tracking-widest hover:bg-gray-200 dark:hover:bg-gray-600 transition-all flex items-center justify-center gap-2">
          <Icon icon="mdi:refresh" :class="{'animate-spin': loading}" class="text-lg" />
          <span class="hidden sm:inline">Actualizar</span>
        </button>
      </div>
    </div>

    <!-- ESTADÍSTICAS PREMIUM -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 mb-6 animate-fadeInUp delay-100">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-5 relative overflow-hidden group">
        <div class="absolute top-0 right-0 w-24 h-24 bg-blue-500/5 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
        <div class="flex items-start justify-between relative z-10">
          <div>
            <p class="text-[10px] font-black text-gray-400 tracking-widest uppercase mb-1">Total Zonas</p>
            <p class="text-3xl font-black text-gray-900 dark:text-white">{{ zonas.length }}</p>
          </div>
          <div class="p-2.5 bg-blue-50 dark:bg-blue-900/30 rounded-xl">
            <Icon icon="mdi:map" class="text-blue-600 dark:text-blue-400 text-2xl" />
          </div>
        </div>
        <div class="mt-3 relative z-10">
          <span class="px-2.5 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg text-[10px] font-black">{{ zonasFiltradas.length }} filtradas</span>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-5 relative overflow-hidden group">
        <div class="absolute top-0 right-0 w-24 h-24 bg-emerald-500/5 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
        <div class="flex items-start justify-between relative z-10">
          <div>
            <p class="text-[10px] font-black text-gray-400 tracking-widest uppercase mb-1">Activas</p>
            <p class="text-3xl font-black text-emerald-600">{{ estadisticas.activas }}</p>
          </div>
          <div class="p-2.5 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl">
            <Icon icon="mdi:check-decagram" class="text-emerald-600 dark:text-emerald-400 text-2xl" />
          </div>
        </div>
        <div class="mt-3 relative z-10">
          <div class="w-full bg-gray-100 dark:bg-gray-700 h-1.5 rounded-full overflow-hidden">
            <div class="bg-emerald-500 h-full rounded-full transition-all duration-500" :style="{ width: estadisticas.porcentajeActivas + '%' }"></div>
          </div>
          <span class="text-[10px] font-black text-emerald-600 mt-1.5 block">{{ estadisticas.porcentajeActivas }}% del total</span>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-5 relative overflow-hidden group">
        <div class="absolute top-0 right-0 w-24 h-24 bg-rose-500/5 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
        <div class="flex items-start justify-between relative z-10">
          <div>
            <p class="text-[10px] font-black text-gray-400 tracking-widest uppercase mb-1">Inactivas</p>
            <p class="text-3xl font-black text-rose-600">{{ estadisticas.inactivas }}</p>
          </div>
          <div class="p-2.5 bg-rose-50 dark:bg-rose-900/30 rounded-xl">
            <Icon icon="mdi:close-circle" class="text-rose-600 dark:text-rose-400 text-2xl" />
          </div>
        </div>
        <div class="mt-3 relative z-10 flex items-center text-[10px] font-black text-rose-500">
          <Icon icon="mdi:alert-circle" class="mr-1 text-sm" /> Zonas desactivadas
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-5 relative overflow-hidden group">
        <div class="absolute top-0 right-0 w-24 h-24 bg-amber-500/5 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
        <div class="flex items-start justify-between relative z-10">
          <div>
            <p class="text-[10px] font-black text-gray-400 tracking-widest uppercase mb-1">Total Mesas</p>
            <p class="text-3xl font-black text-amber-600">{{ estadisticas.totalMesas }}</p>
          </div>
          <div class="p-2.5 bg-amber-50 dark:bg-amber-900/30 rounded-xl">
            <Icon icon="mdi:table-chair" class="text-amber-600 dark:text-amber-400 text-2xl" />
          </div>
        </div>
        <div class="mt-3 relative z-10 flex items-center text-[10px] font-black text-amber-500">
          <Icon icon="mdi:chair-rolling" class="mr-1 text-sm" /> Mesas registradas
        </div>
      </div>
    </div>

    <!-- CONTENIDO -->
    <div class="animate-fadeInUp delay-200">
      <!-- VISTA TABLA -->
      <div v-if="viewMode === 'table'" class="hidden lg:block bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div class="overflow-x-auto custom-scrollbar">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="bg-gray-50/80 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700">
                <th class="px-6 py-4 text-[11px] font-black text-gray-400 tracking-widest uppercase">Zona</th>
                <th class="px-6 py-4 text-[11px] font-black text-gray-400 tracking-widest uppercase">Descripción</th>
                <th class="px-6 py-4 text-[11px] font-black text-gray-400 tracking-widest uppercase text-center">Mesas</th>
                <th class="px-6 py-4 text-[11px] font-black text-gray-400 tracking-widest uppercase text-center">Estado</th>
                <th class="px-6 py-4 text-[11px] font-black text-gray-400 tracking-widest uppercase">Creación</th>
                <th class="px-6 py-4 text-[11px] font-black text-gray-400 tracking-widest uppercase text-right">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
              <tr v-if="loading" v-for="i in 3" :key="'load-'+i" class="animate-pulse">
                <td colspan="6" class="px-6 py-5"><div class="h-5 bg-gray-100 dark:bg-gray-700 rounded-lg w-full"></div></td>
              </tr>
              <tr v-if="!loading && zonasFiltradas.length === 0">
                <td colspan="6" class="px-6 py-20 text-center">
                  <div class="flex flex-col items-center opacity-40 select-none">
                    <Icon icon="mdi:map-marker-off" class="text-8xl mb-4 text-gray-300 dark:text-gray-600" />
                    <h4 class="text-xl font-black text-gray-400 tracking-widest uppercase">No hay zonas</h4>
                    <p class="text-sm text-gray-400 mt-1 font-medium">Registra tu primera zona para comenzar</p>
                  </div>
                </td>
              </tr>
              <tr v-for="zona in zonasFiltradas" :key="zona.idZonaMesa" class="hover:bg-blue-50/40 dark:hover:bg-gray-700/40 transition-all duration-200 group">
                <td class="px-6 py-4">
                  <div class="flex items-center gap-3">
                    <div :class="['h-11 w-11 rounded-xl flex items-center justify-center shadow-sm border', zona.estado?.toLowerCase() === 'activo' ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100 dark:from-blue-900/40 dark:to-indigo-900/40 dark:border-blue-800/50' : 'bg-gray-50 border-gray-100 dark:bg-gray-800 dark:border-gray-700']">
                      <Icon icon="mdi:map" :class="['text-xl', zona.estado?.toLowerCase() === 'activo' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400']" />
                    </div>
                    <div>
                      <div class="text-sm font-black text-gray-900 dark:text-white">{{ zona.nombre }}</div>
                      <div class="text-[11px] font-bold text-gray-400">ID: {{ zona.idZonaMesa }}</div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4">
                  <span class="text-sm font-medium text-gray-600 dark:text-gray-300 truncate max-w-[250px] block">{{ zona.descripcion || 'Sin descripción' }}</span>
                </td>
                <td class="px-6 py-4 text-center">
                  <span class="px-3 py-1.5 inline-flex text-[11px] font-black uppercase tracking-widest rounded-lg bg-blue-50 text-blue-700 border border-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/50">
                    <Icon icon="mdi:table-chair" class="mr-1.5 text-sm" /> {{ zona.totalMesas || 0 }}
                  </span>
                </td>
                <td class="px-6 py-4 text-center">
                  <span :class="['px-3 py-1.5 inline-flex text-[11px] font-black uppercase tracking-widest rounded-lg border', zona.estado?.toLowerCase() === 'activo' ? 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/50' : 'bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800/50']">
                    <Icon :icon="zona.estado?.toLowerCase() === 'activo' ? 'mdi:check-circle' : 'mdi:close-circle'" class="mr-1.5 text-sm" />
                    {{ convertirEstadoATexto(zona.estado) }}
                  </span>
                </td>
                <td class="px-6 py-4">
                  <span class="text-xs font-bold text-gray-500 dark:text-gray-400">{{ formatearFechaZona(zona.fecha_creacion) }}</span>
                </td>
                <td class="px-6 py-4 text-right">
                  <div class="flex items-center justify-end gap-2">
                    <button @click.stop="openEditModal(zona)" title="Editar" class="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-amber-500 rounded-lg hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200 transition-all shadow-sm">
                      <Icon icon="mdi:pencil" class="text-lg" />
                    </button>
                    <button @click.stop="abrirModalToggleStatus(zona)" :title="zona.estado?.toLowerCase() === 'activo' ? 'Desactivar' : 'Activar'" class="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-indigo-500 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200 transition-all shadow-sm">
                      <Icon :icon="zona.estado?.toLowerCase() === 'activo' ? 'mdi:eye-off' : 'mdi:eye'" class="text-lg" />
                    </button>
                    <button @click.stop="abrirModalEliminar(zona)" title="Eliminar" class="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-rose-500 rounded-lg hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition-all shadow-sm">
                      <Icon icon="mdi:delete" class="text-lg" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- VISTA TARJETAS -->
      <div v-if="viewMode === 'cards' || windowWidth < 1024" class="lg:grid lg:grid-cols-2 lg:gap-4 xl:grid-cols-3 space-y-4 lg:space-y-0">
        <div v-if="loading" v-for="i in 3" :key="'load-mobile-'+i" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-5 animate-pulse h-40"></div>
        <div v-if="!loading && zonasFiltradas.length === 0" class="col-span-full bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-10 text-center">
          <div class="flex flex-col items-center opacity-40 select-none">
            <Icon icon="mdi:map-marker-off" class="text-6xl mb-3 text-gray-300 dark:text-gray-600" />
            <h4 class="text-lg font-black text-gray-400 tracking-widest uppercase">No hay zonas</h4>
            <p class="text-sm text-gray-400 mt-1">Crea una nueva zona para empezar</p>
          </div>
        </div>
        
        <div v-for="zona in zonasFiltradas" :key="'card-'+zona.idZonaMesa" class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-all duration-300 group flex flex-col">
          <div class="p-5 flex-1">
            <div class="flex items-start justify-between mb-4">
              <div class="flex items-center gap-3">
                <div :class="['w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm border', zona.estado?.toLowerCase() === 'activo' ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100 dark:from-blue-900/40 dark:to-indigo-900/40 dark:border-blue-800/50' : 'bg-gray-50 border-gray-100 dark:bg-gray-800 dark:border-gray-700']">
                  <Icon icon="mdi:map" :class="['text-2xl', zona.estado?.toLowerCase() === 'activo' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400']" />
                </div>
                <div>
                  <h3 class="font-black text-gray-900 dark:text-white text-base">{{ zona.nombre }}</h3>
                  <p class="text-xs font-bold text-gray-400 mt-0.5">ID: {{ zona.idZonaMesa }}</p>
                </div>
              </div>
              <span :class="['px-2.5 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg border', zona.estado?.toLowerCase() === 'activo' ? 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/50' : 'bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800/50']">
                {{ convertirEstadoATexto(zona.estado) }}
              </span>
            </div>
            <p class="text-sm font-medium text-gray-500 dark:text-gray-400 mb-4 line-clamp-2">{{ zona.descripcion || 'Sin descripción' }}</p>
            <div class="grid grid-cols-2 gap-3 mb-2">
              <div class="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3 border border-gray-100 dark:border-gray-700/50">
                <p class="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Mesas</p>
                <p class="text-lg font-black text-blue-600">{{ zona.totalMesas || 0 }}</p>
              </div>
              <div class="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3 border border-gray-100 dark:border-gray-700/50">
                <p class="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Creada</p>
                <p class="text-[11px] font-bold text-gray-700 dark:text-gray-300 mt-1">{{ formatearFechaZona(zona.fecha_creacion) }}</p>
              </div>
            </div>
          </div>
          <div class="bg-gray-50/50 dark:bg-gray-900/30 border-t border-gray-100 dark:border-gray-700 p-3 grid grid-cols-3 gap-2">
            <button @click.stop="openEditModal(zona)" class="py-2.5 bg-white dark:bg-gray-800 text-amber-600 border border-gray-200 dark:border-gray-600 rounded-xl font-black text-[10px] flex flex-col sm:flex-row items-center justify-center gap-1 hover:bg-amber-50 transition-colors">
              <Icon icon="mdi:pencil" class="text-base sm:text-sm" /><span>Editar</span>
            </button>
            <button @click.stop="abrirModalToggleStatus(zona)" class="py-2.5 bg-white dark:bg-gray-800 text-indigo-600 border border-gray-200 dark:border-gray-600 rounded-xl font-black text-[10px] flex flex-col sm:flex-row items-center justify-center gap-1 hover:bg-indigo-50 transition-colors">
              <Icon :icon="zona.estado?.toLowerCase() === 'activo' ? 'mdi:eye-off' : 'mdi:eye'" class="text-base sm:text-sm" /><span>{{ zona.estado?.toLowerCase() === 'activo' ? 'Desactivar' : 'Activar' }}</span>
            </button>
            <button @click.stop="abrirModalEliminar(zona)" class="py-2.5 bg-white dark:bg-gray-800 text-rose-600 border border-gray-200 dark:border-gray-600 rounded-xl font-black text-[10px] flex flex-col sm:flex-row items-center justify-center gap-1 hover:bg-rose-50 transition-colors">
              <Icon icon="mdi:delete" class="text-base sm:text-sm" /><span>Eliminar</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL FORMULARIO -->
    <Teleport to="body">
      <div v-if="showModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] flex items-end sm:items-center justify-center sm:p-4" @click.self="closeModal">
        <div class="bg-white dark:bg-gray-800 w-full max-w-lg sm:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden animate-slideUp sm:animate-zoomIn flex flex-col max-h-[90vh]">
          <div class="bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-4 flex items-center gap-3 shrink-0">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon :icon="editingZona ? 'mdi:pencil-box' : 'mdi:map-plus'" class="text-white text-xl" />
            </div>
            <div>
              <h3 class="text-base font-black text-white">{{ editingZona ? 'Editar Zona' : 'Nueva Zona' }}</h3>
              <p class="text-[11px] text-white/80 font-bold">{{ editingZona ? 'Modifica los datos' : 'Completa los datos' }}</p>
            </div>
            <button @click="closeModal" class="ml-auto p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-all">
              <Icon icon="mdi:close" class="text-white text-xl" />
            </button>
          </div>
          <div class="p-5 overflow-y-auto custom-scrollbar">
            <form @submit.prevent="saveZona" class="space-y-5">
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 uppercase">Nombre de la Zona *</label>
                <input v-model="form.nombre" type="text" required maxlength="100" class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none transition-all" placeholder="Ej: Terraza, Salón Principal" />
              </div>
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 uppercase">Descripción</label>
                <textarea v-model="form.descripcion" rows="3" maxlength="200" class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-sm font-medium text-gray-800 dark:text-white outline-none transition-all resize-none" placeholder="Descripción opcional..."></textarea>
              </div>
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-3 uppercase">Estado</label>
                <div class="grid grid-cols-2 gap-3">
                  <label :class="['flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-black text-xs cursor-pointer transition-all', form.estado === 'activo' ? 'bg-emerald-50 border-emerald-500 text-emerald-700 dark:bg-emerald-900/30' : 'bg-white border-gray-200 text-gray-500 dark:bg-gray-800 dark:border-gray-700']">
                    <input type="radio" v-model="form.estado" value="activo" class="hidden" /> Activo
                  </label>
                  <label :class="['flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-black text-xs cursor-pointer transition-all', form.estado === 'inactivo' ? 'bg-rose-50 border-rose-500 text-rose-700 dark:bg-rose-900/30' : 'bg-white border-gray-200 text-gray-500 dark:bg-gray-800 dark:border-gray-700']">
                    <input type="radio" v-model="form.estado" value="inactivo" class="hidden" /> Inactivo
                  </label>
                </div>
              </div>
              <div class="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button type="button" @click="closeModal" class="w-full sm:w-auto px-6 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-gray-50 transition-all">Cancelar</button>
                <button type="submit" :disabled="saving" class="w-full flex-1 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:opacity-90 transition-all shadow-lg shadow-blue-500/30 disabled:opacity-50 flex items-center justify-center gap-2">
                  <Icon :icon="saving ? 'mdi:loading' : 'mdi:content-save'" :class="{'animate-spin': saving}" class="text-lg" />
                  {{ saving ? 'Guardando...' : (editingZona ? 'Actualizar Zona' : 'Crear Zona') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- MODAL DE CONFIRMACIÓN PERSONALIZADO (Solo para Eliminar/Desactivar) -->
    <Teleport to="body">
      <div v-if="showConfirmModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10002] flex items-end sm:items-center justify-center sm:p-4" @click.self="cancelarConfirmacion">
        <div class="bg-white dark:bg-gray-800 w-full max-w-md sm:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden animate-slideUp sm:animate-zoomIn">
          <div class="px-5 py-4" :class="confirmModalColor">
            <div class="flex items-center gap-3">
              <div class="p-2 bg-white/20 rounded-xl">
                <Icon :icon="confirmModalIcon" class="text-white text-xl" />
              </div>
              <div>
                <h3 class="text-base font-black text-white">{{ confirmModalTitulo }}</h3>
                <p class="text-[11px] text-white/80 font-bold">{{ confirmModalSubtitulo }}</p>
              </div>
            </div>
          </div>
          <div class="p-6 text-center">
            <p class="text-sm font-bold text-gray-700 dark:text-gray-300">{{ confirmModalMensaje }}</p>
            <p v-if="confirmModalDetalle" class="text-xs text-gray-500 dark:text-gray-400 mt-2">{{ confirmModalDetalle }}</p>
          </div>
          <div class="px-5 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 flex flex-col-reverse sm:flex-row gap-3">
            <button @click="cancelarConfirmacion" class="w-full sm:flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-gray-100 transition-all">Cancelar</button>
            <button @click="ejecutarAccionConfirmada" :disabled="confirmModalCargando" class="w-full sm:flex-[2] py-3 rounded-xl font-black text-xs uppercase tracking-widest hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-lg" :class="confirmModalButtonColor">
              <Icon :icon="confirmModalCargando ? 'mdi:loading' : 'mdi:check'" :class="{'animate-spin': confirmModalCargando}" class="text-lg" />
              {{ confirmModalCargando ? 'Procesando...' : confirmModalBotonTexto }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import Swal from 'sweetalert2'; // 👈 IMPORTAMOS SWEETALERT2
import {
  obtenerZonas, crearZona, actualizarZona, eliminarZona, activarZona, desactivarZona,
  formatearFechaZona, convertirEstadoATexto, obtenerEstadisticasZonas, buscarZonas, filtrarZonasPorEstado,
} from '@/api/zonasMesa.js';

export default {
  name: 'ZonasMesaList',
  components: { Icon },
  data() {
    return {
      zonas: [], loading: false, saving: false, showModal: false,
      searchQuery: '', filterStatus: 'all', viewMode: 'table', windowWidth: window.innerWidth,
      form: { nombre: '', descripcion: '', estado: 'activo' }, editingZona: null,
      
      // Variables para el modal de confirmación (solo para acciones destructivas)
      showConfirmModal: false, confirmModalTitulo: '', confirmModalSubtitulo: '',
      confirmModalMensaje: '', confirmModalDetalle: '', confirmModalIcon: '',
      confirmModalColor: '', confirmModalButtonColor: '', confirmModalBotonTexto: '',
      confirmModalCargando: false, confirmModalAccion: null, confirmModalDatos: null,
    };
  },
  computed: {
    zonasFiltradas() {
      let filtered = [...this.zonas];
      if (this.searchQuery.trim()) filtered = buscarZonas(filtered, this.searchQuery);
      filtered = filtrarZonasPorEstado(filtered, this.filterStatus);
      return filtered;
    },
    estadisticas() { return obtenerEstadisticasZonas(this.zonas); },
  },
  mounted() {
    this.cargarZonas();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() { window.removeEventListener('resize', this.handleResize); },
  methods: {
    handleResize() { this.windowWidth = window.innerWidth; },
    
    // Helper para mostrar notificaciones con SweetAlert2
    mostrarToast(icon, title, text = '') {
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: icon,
        title: title,
        text: text,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
      });
    },

    async cargarZonas() {
      this.loading = true;
      try {
        const response = await obtenerZonas();
        if (response.success) this.zonas = response.data || [];
      } catch (error) { console.error('Error al cargar zonas:', error); }
      finally { this.loading = false; }
    },
    openCreateModal() {
      this.editingZona = null;
      this.form = { nombre: '', descripcion: '', estado: 'activo' };
      this.showModal = true;
    },
    openEditModal(zona) {
      this.editingZona = zona;
      this.form = { nombre: zona.nombre, descripcion: zona.descripcion, estado: zona.estado };
      this.showModal = true;
    },
    closeModal() { this.showModal = false; },
    
    async saveZona() {
      this.saving = true;
      try {
        let result;
        if (this.editingZona) result = await actualizarZona(this.editingZona.idZonaMesa, this.form);
        else result = await crearZona(this.form);
        
        if (result.success) {
          await this.cargarZonas();
          this.closeModal();
          // 👇 Notificación de éxito con SweetAlert
          this.mostrarToast('success', '¡Éxito!', `La zona se ha ${this.editingZona ? 'actualizado' : 'creado'} correctamente.`);
        } else {
          this.closeModal();
          // 👇 Notificación de error con SweetAlert
          this.mostrarToast('error', 'Error al guardar', result.error || 'Ocurrió un error al guardar la zona.');
        }
      } catch (error) {
        this.mostrarToast('error', 'Error inesperado', error.message || 'Ocurrió un error al guardar la zona.');
      } finally { this.saving = false; }
    },
    
    abrirModalEliminar(zona) {
      // Usamos el modal personalizado solo para confirmar
      this.abrirModalConfirmacion({
        titulo: 'Eliminar Zona', subtitulo: 'Esta acción es permanente',
        mensaje: `¿Eliminar la zona "${zona.nombre}"?`,
        detalle: 'La zona se borrará de la base de datos. Esta acción no se puede deshacer.',
        icon: 'mdi:alert-circle', color: 'bg-gradient-to-r from-red-600 to-pink-600',
        buttonColor: 'bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700',
        botonTexto: 'Eliminar', datos: zona.idZonaMesa, accion: (id) => this.deleteZona(id)
      });
    },
    
    async deleteZona(id) {
      try {
        const result = await eliminarZona(id);
        
        if (result.success) {
          await this.cargarZonas();
          // 👇 Notificación de éxito con SweetAlert
          this.mostrarToast('success', '¡Eliminada!', 'La zona ha sido eliminada permanentemente.');
        } else {
          // 👇 Notificación de error con SweetAlert (aquí saldrá si el backend rechaza)
          this.mostrarToast('error', 'No se pudo eliminar', result.error || 'No se puede eliminar la zona.');
        }
      } catch (error) {
        this.mostrarToast('error', 'Error inesperado', error.message || 'Ocurrió un error al eliminar la zona.');
      }
    },
    
    abrirModalToggleStatus(zona) {
      const esActivo = zona.estado?.toLowerCase() === 'activo';
      this.abrirModalConfirmacion({
        titulo: esActivo ? 'Desactivar Zona' : 'Activar Zona',
        subtitulo: `Cambiar estado de la zona`,
        mensaje: `¿${esActivo ? 'desactivar' : 'activar'} "${zona.nombre}"?`,
        detalle: `Pasará de "${convertirEstadoATexto(zona.estado)}" a "${convertirEstadoATexto(esActivo ? 'inactivo' : 'activo')}".`,
        icon: esActivo ? 'mdi:close-circle' : 'mdi:check-circle',
        color: esActivo ? 'bg-gradient-to-r from-orange-500 to-amber-600' : 'bg-gradient-to-r from-green-600 to-emerald-600',
        buttonColor: esActivo ? 'bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700' : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700',
        botonTexto: esActivo ? 'Desactivar' : 'Activar',
        datos: zona, accion: (z) => this.toggleStatus(z)
      });
    },
    
    async toggleStatus(zona) {
      const esActivo = zona.estado?.toLowerCase() === 'activo';
      try {
        let result;
        if (esActivo) result = await desactivarZona(zona.idZonaMesa);
        else result = await activarZona(zona.idZonaMesa);
        
        if (result.success) {
          await this.cargarZonas();
          // 👇 Notificación de éxito con SweetAlert
          this.mostrarToast('success', '¡Actualizada!', `La zona ha sido ${esActivo ? 'desactivada' : 'activada'} correctamente.`);
        } else {
          this.mostrarToast('error', 'Error al actualizar', result.error || 'Ocurrió un error al cambiar el estado.');
        }
      } catch (error) {
        this.mostrarToast('error', 'Error inesperado', error.message || 'Ocurrió un error al cambiar el estado.');
      }
    },
    
    // ========== MODAL DE CONFIRMACIÓN PERSONALIZADO ==========
    abrirModalConfirmacion(config) {
      this.confirmModalTitulo = config.titulo || 'Confirmar';
      this.confirmModalSubtitulo = config.subtitulo || '';
      this.confirmModalMensaje = config.mensaje || '¿Estás seguro?';
      this.confirmModalDetalle = config.detalle || '';
      this.confirmModalIcon = config.icon || 'mdi:help-circle';
      this.confirmModalColor = config.color || 'bg-gradient-to-r from-blue-600 to-indigo-600';
      this.confirmModalButtonColor = config.buttonColor || 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700';
      this.confirmModalBotonTexto = config.botonTexto || 'Confirmar';
      this.confirmModalAccion = config.accion;
      this.confirmModalDatos = config.datos || null;
      this.showConfirmModal = true;
    },
    cancelarConfirmacion() {
      this.showConfirmModal = false;
      this.confirmModalCargando = false;
      this.confirmModalAccion = null;
    },
    async ejecutarAccionConfirmada() {
      if (this.confirmModalAccion) {
        this.confirmModalCargando = true;
        await this.confirmModalAccion(this.confirmModalDatos);
        this.confirmModalCargando = false;
        this.cancelarConfirmacion();
      }
    },
    
    formatearFechaZona,
    convertirEstadoATexto,
  },
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
.custom-scrollbar::-webkit-scrollbar { height: 6px; width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #475569; }

@keyframes zoomIn { from { opacity: 0; transform: scale(0.95) translateY(10px); } to { opacity: 1; transform: scale(1) translateY(0); } }
.animate-zoomIn { animation: zoomIn 0.25s cubic-bezier(0.16, 1, 0.3, 1); }

@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
.animate-slideUp { animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1); }

@keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeInUp { animation: fadeInUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
.delay-100 { animation-delay: 0.1s; }
.delay-200 { animation-delay: 0.2s; }

@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }

@media (max-width: 768px) {
  button { min-height: 44px; }
  input, select, textarea { font-size: 16px !important; }
}
</style>